import type { MainViewCameraControlsRef, ICameraControls } from '../ModelViewer';
import type React from 'react';
import type * as THREE from 'three';

export type ScanReviewViewState = Record<ScanReviewPanelType, ScanReviewPanelCameraState | null>;

export enum ScanReviewViewType {
    Single = 'single',
    SideBySide = 'side-by-side',
    Complete = 'complete',
}

export enum ScanReviewPanelType {
    Upper = 'upper',
    Lower = 'lower',
    Left = 'left',
    Right = 'right',
    Front = 'front',
}

export enum ScanReviewDisplayType {
    Scan = 'scan',
    StoneModel = 'stone-model',
    BiteAnalysis = 'bite-analysis',
}

export type ScanReviewMarginMarkingDisplayType = ScanReviewDisplayType.Scan | ScanReviewDisplayType.StoneModel;

export interface ScanReviewViewJawDisplayState {
    upper: boolean;
    lower: boolean;
}

export interface ScanReviewViewJawDisplayState {
    upper: boolean;
    lower: boolean;
}

export class ScanReviewPanelCameraState {
    constructor(
        public position: THREE.Vector3,
        public rotation: THREE.Euler,
        public up: THREE.Vector3,
        public zoom: number,
    ) {}
}

export class ScanReviewViewManager {
    constructor(
        public canvasRef: React.MutableRefObject<HTMLCanvasElement | null>,
        public cameraRef: React.MutableRefObject<THREE.OrthographicCamera | null>,
        public cameraControlsRef: MainViewCameraControlsRef,
        public viewState: ScanReviewViewState,
    ) {}

    initializeViewState(controls: ICameraControls, panelType: ScanReviewPanelType) {
        if (!controls) {
            return;
        }
        this.cameraControlsRef.current = controls;
        const panelViewState = this.viewState[panelType];
        if (panelViewState !== null) {
            controls.reset();
            controls.object.position.copy(panelViewState.position);
            controls.object.rotation.copy(panelViewState.rotation);
            controls.object.up.copy(panelViewState.up);
            controls.object.zoom = panelViewState.zoom;
            controls.object.updateProjectionMatrix();
            controls.update();
        }
        const eventTypes = ['change', 'start', 'end', 'zoomChange'];
        for (const eventType of eventTypes) {
            controls.addEventListener(eventType, event => {
                const eventControls = event.target as ICameraControls;
                const currentCameraState = new ScanReviewPanelCameraState(
                    eventControls.object.position.clone(),
                    eventControls.object.rotation.clone(),
                    eventControls.object.up.clone(),
                    eventControls.object.zoom,
                );
                this.viewState[panelType] = currentCameraState;
            });
        }
    }

    get canvas() {
        return this.canvasRef.current;
    }

    get camera() {
        return this.cameraRef.current;
    }

    get controls() {
        return this.cameraControlsRef.current;
    }
}
